import { useQuery } from '@tanstack/react-query';
import { AccessorKeyColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { Link, Route, Routes } from 'react-router-dom';

import { getStockAdjustments } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import PaginationControls from '../../components/Pagination/PaginationControls';
import getStatusColor from '../../helpers/getStatusColor';
import { StockAdjustment as StockAdjustmentType } from '../../types';
import { formatDateShort } from '../../utils/formatShortDate';
import { formatTime } from '../../utils/formatTime';
import AnalyticsHeader from '../../widgets/Header';
import CreateStockAdjustmentModal from './CreateStockAdjustmentModal';

type STOCK_ADJUSTMENT_TYPE = 'depleting' | 'additive';

interface StockAdjustmentTableProps {
  query?: Record<'type', STOCK_ADJUSTMENT_TYPE>;
}

export const STOCK_ADJUSTMENTS_QUERY_KEY = ['stockAdjustments'];

const CreateStockAdjustmentButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}> Create Stock Adjustment </Button>;
};

const columns: AccessorKeyColumnDef<StockAdjustmentType, any>[] = [
  {
    accessorKey: 'productId.name',
    header: 'Product',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  },
  {
    accessorKey: 'orderId',
    header: 'Order',
    cell: info => (info.getValue() ? <Link to={`/order/${info.getValue()}`}>View Order</Link> : null),
    footer: info => info.column.id ?? ''
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  },
  {
    accessorKey: 'cumulativeQuantity',
    header: 'Cumulative Quantity',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: info => <Badge bg={`${getStatusColor(info.getValue())}-soft`}>{info.getValue()}</Badge>,
    footer: info => info.column.id ?? ''
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: info => formatDateShort(info.getValue()),
    footer: info => info.column.id ?? ''
  },
  {
    accessorKey: 'createdAt',
    header: 'Created Time',
    id: 'createdTime',
    cell: info => formatTime(info.getValue()),
    footer: info => info.column.id ?? ''
  }
];

const limit = 100;

const StockAdjustmentTable = ({ query }: StockAdjustmentTableProps) => {
  const [page, setPage] = useState(1);
  const [showCreateStockAdjustmentModal, setShowCreateStockAdjustmentModal] = useState(false);
  const compositeQueryKey = query
    ? [...STOCK_ADJUSTMENTS_QUERY_KEY, { ...query, page, limit }]
    : [STOCK_ADJUSTMENTS_QUERY_KEY, { page, limit }];
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getStockAdjustments({ ...query, page, limit })
  });
  const stockAdjustments = data?.results ?? [];

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { count } = data ?? {};

  const totalPages = Math.ceil(count! / limit);

  const nextPage = data?.nextPage;
  const prevPage = data?.prevPage;

  const onCreateButtonClicked = () => {
    setShowCreateStockAdjustmentModal(true);
  };

  const onCreateModalClosed = () => {
    setShowCreateStockAdjustmentModal(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  let noStocksMessage;
  switch (query?.type) {
    case 'depleting':
      noStocksMessage = stockAdjustments && stockAdjustments.length === 0 && (
        <EmptyTableState message="No depleting stock yet." />
      );
      break;
    case 'additive':
      noStocksMessage = stockAdjustments && stockAdjustments.length === 0 && (
        <EmptyTableState message="No additive stock yet." />
      );
      break;
    default:
      noStocksMessage = null;
  }

  return (
    <>
      {showCreateStockAdjustmentModal && (
        <CreateStockAdjustmentModal show={showCreateStockAdjustmentModal} handleClose={onCreateModalClosed} />
      )}
      <AnalyticsHeader
        title="Stock Adjustments"
        headerButton={<CreateStockAdjustmentButton onClick={onCreateButtonClicked} />}
        tabs={tabRoutes}
      />
      <div className="main-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12}>
              {isLoading ? (
                <Loader />
              ) : noStocksMessage ? (
                noStocksMessage
              ) : stockAdjustments && stockAdjustments.length > 0 ? (
                <>
                  <TableList stockAdjustments={stockAdjustments} columns={columns} />
                  <PaginationControls
                    currentPage={page}
                    totalPages={totalPages ?? 0}
                    onPageChange={handlePageChange}
                    nextPage={nextPage ?? null}
                    prevPage={prevPage ?? null}
                  />
                </>
              ) : (
                <EmptyTableState message="No stock adjustment yet." />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

const tabRoutes = [
  {
    title: 'All',
    path: 'all',
    tabLink: '/stock-adjustment/all',
    element: <StockAdjustmentTable />
  },
  {
    title: 'Depleting',
    path: 'depleting',
    tabLink: '/stock-adjustment/depleting',
    element: <StockAdjustmentTable query={{ type: 'depleting' }} />
  },
  {
    title: 'Additive',
    path: 'additive',
    tabLink: '/stock-adjustment/additive',
    element: <StockAdjustmentTable query={{ type: 'additive' }} />
  }
];

const StockAdjustment = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default StockAdjustment;

interface TableListProps {
  stockAdjustments: StockAdjustmentType[];
  columns: AccessorKeyColumnDef<StockAdjustmentType, any>[];
}

const TableList = ({ stockAdjustments, columns }: TableListProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);

  const tableInstance = useReactTable({
    data: stockAdjustments!,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter
  });

  return (
    <Card>
      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
